import { format, formatDistanceToNow, isAfter, isBefore, parseISO } from 'date-fns';
import { clsx, type ClassValue } from 'clsx';
import type { SystemType, AgentType, OrderStatus, AgentStatus } from '@/types';

// Class name utility
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

// Date formatting utilities
export const formatters = {
  date: (date: string | Date): string => {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, 'MMM d, yyyy');
  },

  dateTime: (date: string | Date): string => {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, 'MMM d, yyyy HH:mm:ss');
  },

  time: (date: string | Date): string => {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, 'HH:mm:ss');
  },

  timeAgo: (date: string | Date): string => {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return formatDistanceToNow(dateObj, { addSuffix: true });
  },

  duration: (milliseconds: number): string => {
    if (milliseconds < 1000) {
      return `${Math.round(milliseconds)}ms`;
    }
    if (milliseconds < 60000) {
      return `${(milliseconds / 1000).toFixed(1)}s`;
    }
    if (milliseconds < 3600000) {
      return `${(milliseconds / 60000).toFixed(1)}m`;
    }
    return `${(milliseconds / 3600000).toFixed(1)}h`;
  },
};

// Number formatting utilities
export const numberFormatters = {
  currency: (amount: number, currency = 'USD'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  },

  decimal: (value: number, decimals = 2): string => {
    return value.toFixed(decimals);
  },

  percentage: (value: number, decimals = 1): string => {
    return `${(value * 100).toFixed(decimals)}%`;
  },

  compact: (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  },
};

// Status utilities
export const statusUtils = {
  getSystemDisplayName: (systemType: SystemType): string => {
    const names: Record<SystemType, string> = {
      gkpos: 'GK POS',
      sap_ecc: 'SAP ECC',
      hybris: 'Hybris Commerce',
    };
    return names[systemType] || systemType.toUpperCase();
  },

  getAgentDisplayName: (agentType: AgentType): string => {
    const names: Record<AgentType, string> = {
      monitor: 'Monitor Agent',
      verify: 'Verify Agent',
      execute: 'Execute Agent',
      learn: 'Learn Agent',
    };
    return names[agentType] || agentType;
  },

  getOrderStatusColor: (status: OrderStatus): string => {
    const colors: Record<OrderStatus, string> = {
      pending: 'status-idle',
      processing: 'status-active',
      completed: 'status-healthy',
      cancelled: 'status-warning',
      failed: 'status-critical',
    };
    return colors[status] || 'status-idle';
  },

  getAgentStatusColor: (status: AgentStatus): string => {
    const colors: Record<AgentStatus, string> = {
      idle: 'status-idle',
      monitoring: 'status-active',
      analyzing: 'status-active',
      executing: 'status-active',
      learning: 'status-active',
      error: 'status-critical',
      disabled: 'status-warning',
    };
    return colors[status] || 'status-idle';
  },

  getHealthStatusColor: (isHealthy: boolean): string => {
    return isHealthy ? 'status-healthy' : 'status-critical';
  },
};

// Validation utilities
export const validators = {
  email: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  orderId: (orderId: string): boolean => {
    const orderIdRegex = /^[A-Z0-9-]{3,20}$/i;
    return orderIdRegex.test(orderId);
  },

  phone: (phone: string): boolean => {
    const phoneRegex = /^\+?[\d\s\-()]{10,}$/;
    return phoneRegex.test(phone);
  },

  amount: (amount: string | number): boolean => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return !isNaN(numAmount) && numAmount >= 0;
  },
};

// Data transformation utilities
export const transformers = {
  camelToSnakeCase: (str: string): string => {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
  },

  snakeToCamelCase: (str: string): string => {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  },

  objectKeysToCamelCase: <T>(obj: Record<string, any>): T => {
    const result: Record<string, any> = {};
    
    for (const [key, value] of Object.entries(obj)) {
      const camelKey = transformers.snakeToCamelCase(key);
      
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result[camelKey] = transformers.objectKeysToCamelCase(value);
      } else if (Array.isArray(value)) {
        result[camelKey] = value.map(item =>
          item && typeof item === 'object' ? transformers.objectKeysToCamelCase(item) : item
        );
      } else {
        result[camelKey] = value;
      }
    }
    
    return result as T;
  },

  objectKeysToSnakeCase: <T>(obj: Record<string, any>): T => {
    const result: Record<string, any> = {};
    
    for (const [key, value] of Object.entries(obj)) {
      const snakeKey = transformers.camelToSnakeCase(key);
      
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result[snakeKey] = transformers.objectKeysToSnakeCase(value);
      } else if (Array.isArray(value)) {
        result[snakeKey] = value.map(item =>
          item && typeof item === 'object' ? transformers.objectKeysToSnakeCase(item) : item
        );
      } else {
        result[snakeKey] = value;
      }
    }
    
    return result as T;
  },
};

// Local storage utilities
export const storage = {
  get: <T>(key: string, defaultValue: T): T => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },

  set: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Silently handle localStorage errors
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      // Silently handle localStorage errors
    }
  },

  clear: (): void => {
    try {
      localStorage.clear();
    } catch (error) {
      // Silently handle localStorage errors
    }
  },
};

// Array utilities
export const arrayUtils = {
  groupBy: <T, K extends keyof T>(array: T[], key: K): Record<string, T[]> => {
    return array.reduce((groups, item) => {
      const group = String(item[key]);
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(item);
      return groups;
    }, {} as Record<string, T[]>);
  },

  sortBy: <T>(array: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] => {
    return [...array].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  },

  unique: <T>(array: T[], key?: keyof T): T[] => {
    if (!key) {
      return [...new Set(array)];
    }
    
    const seen = new Set();
    return array.filter(item => {
      const value = item[key];
      if (seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
  },
};

// Debounce utility
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

// Throttle utility
export function throttle<T extends (...args: any[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Error handling utilities
export const errorUtils = {
  getErrorMessage: (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  },

  isNetworkError: (error: unknown): boolean => {
    return error instanceof Error && error.message.toLowerCase().includes('network');
  },

  isTimeoutError: (error: unknown): boolean => {
    return error instanceof Error && error.message.toLowerCase().includes('timeout');
  },
};

export default {
  cn,
  formatters,
  numberFormatters,
  statusUtils,
  validators,
  transformers,
  storage,
  arrayUtils,
  debounce,
  throttle,
  errorUtils,
};
